import { prepareAxiosSentryEvent } from "@/utils/sentryApiErrors";

function eventMessages(event, hint) {
  return [
    hint?.originalException?.message,
    event?.message,
    ...(event?.exception?.values || []).map(value => value?.value),
  ].filter(Boolean).join(" ");
}

function eventFrames(event) {
  return (event?.exception?.values || [])
    .flatMap(value => value?.stacktrace?.frames || []);
}

export function hasFirstPartyFrame(event) {
  return eventFrames(event).some(frame => {
    const filename = String(frame?.filename || "");
    return (
      frame?.in_app === true ||
      filename.includes("webpack://invown/src/") ||
      filename.includes("/src/") ||
      filename.includes("invown.com/app/") ||
      filename.includes("dev.invown.com/app/")
    );
  });
}

function hasFirstPartySourceFrame(event) {
  return eventFrames(event).some(frame => {
    const filename = String(frame?.filename || "");
    return (
      frame?.in_app === true ||
      filename.includes("webpack://invown/src/") ||
      filename.includes("/src/")
    );
  });
}

function hasThirdPartyFrame(event, patterns) {
  return eventFrames(event).some(frame => {
    const filename = String(frame?.filename || "");
    return patterns.some(pattern => pattern.test(filename));
  });
}

export function hasExtensionFrame(event) {
  return eventFrames(event).some(frame => {
    const filename = String(frame?.filename || "");
    return (
      filename.startsWith("chrome-extension://") ||
      filename.startsWith("moz-extension://") ||
      filename.startsWith("safari-extension://")
    );
  });
}

export function isMetaMaskNoise(event, hint) {
  const messages = eventMessages(event, hint);

  return hasExtensionFrame(event) && /metamask|failed to connect to metamask|extension not found/i.test(messages);
}

export function isExpectedRouterNavigationFailure(event, hint) {
  const messages = eventMessages(event, hint);

  return /Redirected when going from|Avoided redundant navigation|Navigation cancelled|Navigation aborted/i.test(messages);
}

export function isResizeObserverLoopError(event, hint) {
  return /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i
    .test(eventMessages(event, hint));
}

export function isThirdPartyScriptNoise(event) {
  if (hasFirstPartyFrame(event)) {
    return false;
  }

  return hasThirdPartyFrame(event, [
    /google\.com\/recaptcha/i,
    /gstatic\.com\/recaptcha/i,
    /recaptcha/i,
    /livechatinc\.com/i,
    /cdn\.livechatinc\.com/i,
  ]);
}

export function isExtensionScriptNoise(event) {
  return hasExtensionFrame(event) && !hasFirstPartyFrame(event);
}

export function isNonActionableNetworkNoise(event, hint) {
  if (hasFirstPartyFrame(event) || hint?.originalException?.isAxiosError) {
    return false;
  }

  return /\b(Failed to fetch|NetworkError|Load failed|The network connection was lost)\b/i
    .test(eventMessages(event, hint));
}

export function isUnleashMetricsNoise(event, hint) {
  const messages = eventMessages(event, hint);
  if (!/\b(Failed to fetch|NetworkError|Network Error|Load failed)\b/i.test(messages)) {
    return false;
  }

  return eventFrames(event).some(frame => {
    const filename = String(frame?.filename || "");
    const functionName = String(frame?.function || frame?.functionName || "");
    return (
      /unleash-proxy-client/i.test(filename) ||
      /unleash.*metrics|metrics\.js/i.test(filename) ||
      /sendMetrics|countMetrics|bucketMetrics/i.test(functionName)
    );
  });
}

export function isBooleanPromiseRejection(event, hint) {
  if (hasFirstPartyFrame(event)) {
    return false;
  }

  const original = hint?.originalException;
  if (original === true || original === false) {
    return true;
  }

  return /Non-Error promise rejection captured with value: (true|false)|UnhandledRejection.*\b(true|false)\b/i
    .test(eventMessages(event, hint));
}

export function isBrowserObjectLookupPromiseNoise(event, hint) {
  if (hasFirstPartyFrame(event)) {
    return false;
  }

  return /Object Not Found Matching Id:\s*\d+/i.test(eventMessages(event, hint));
}

export function isInjectedNativeBridgeNoise(event, hint) {
  if (hasFirstPartySourceFrame(event)) {
    return false;
  }

  return /No Listener:\s*tabs:outgoing\.message\.ready|window\.webkit\.messageHandlers|WKWebView API client did not respond|runtime\.sendMessage\(\)\. Tab not found|sendDataToNative/i
    .test(eventMessages(event, hint));
}

function markLowPriorityEvent(event, reason) {
  return {
    ...event,
    level: "warning",
    tags: {
      ...(event.tags || {}),
      low_priority_browser_error: "true",
      low_priority_reason: reason,
    },
    contexts: {
      ...(event.contexts || {}),
      browser_noise: {
        reason,
      },
    },
  };
}

export function isChunkLoadError(errorOrEvent) {
  const message = String(
    errorOrEvent?.message ||
    errorOrEvent?.reason?.message ||
    errorOrEvent?.error?.message ||
    errorOrEvent?.target?.href ||
    errorOrEvent?.target?.src ||
    (errorOrEvent?.exception?.values || []).map(value => value?.value).join(" ") ||
    ""
  );

  return /ChunkLoadError|Loading CSS chunk .* failed|Loading chunk .* failed|\.css\)$|\.js\)$/.test(message);
}

export function sentryBeforeSend(event, hint) {
  const originalException = hint?.originalException;
  const axiosEvent = prepareAxiosSentryEvent(event, hint);
  if (axiosEvent === null) {
    return null;
  }
  if (originalException?.isAxiosError) {
    return axiosEvent;
  }

  if (
    isMetaMaskNoise(event, hint) ||
    isExpectedRouterNavigationFailure(event, hint) ||
    isResizeObserverLoopError(event, hint) ||
    isThirdPartyScriptNoise(event) ||
    isExtensionScriptNoise(event) ||
    isBooleanPromiseRejection(event, hint) ||
    isInjectedNativeBridgeNoise(event, hint)
  ) {
    return null;
  }

  if (isUnleashMetricsNoise(event, hint)) {
    return markLowPriorityEvent(axiosEvent, "unleash_metrics_fetch");
  }

  if (isBrowserObjectLookupPromiseNoise(event, hint)) {
    return markLowPriorityEvent(axiosEvent, "browser_object_lookup");
  }

  if (isChunkLoadError(originalException) || isChunkLoadError(event)) {
    return null;
  }

  if (isNonActionableNetworkNoise(axiosEvent, hint)) {
    return null;
  }

  return axiosEvent;
}
